import {
  createContext,
  type FocusEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "../cx";
import { Icon } from "../Icon";
import styles from "./Toast.module.css";

export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: ReactNode;
}

export interface ToastOptions {
  /** ms; 0 keeps the toast until dismissed. Defaults to TOAST_DURATION, or ERROR_TOAST_DURATION for errors. */
  duration?: number;
}

export interface ToastApi {
  success: (message: ReactNode, options?: ToastOptions) => number;
  error: (message: ReactNode, options?: ToastOptions) => number;
  info: (message: ReactNode, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export const TOAST_DURATION = 5000;
/** Errors carry server text (validation details, retry hints) that takes longer to read (WCAG 2.2.1). */
export const ERROR_TOAST_DURATION = 10000;

interface Timer {
  handle: ReturnType<typeof setTimeout> | null;
  /** ms left when paused; while running, counted from `startedAt`. */
  remaining: number;
  startedAt: number;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, Timer>());
  // Hovering or focusing the stack freezes every countdown, so a toast cannot vanish while it is being read.
  const paused = useRef({ hover: false, focus: false });
  // Mirrors `toasts` synchronously, so two pushes in one tick can see each other.
  const visible = useRef<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t?.handle) clearTimeout(t.handle);
    timers.current.delete(id);
    visible.current = visible.current.filter((x) => x.id !== id);
    setToasts((list) => list.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: ReactNode, options?: ToastOptions) => {
      // The global mutation-error toast and a caller's own catch can report the same failure; show it once.
      if (typeof message === "string") {
        const same = visible.current.find((t) => t.kind === kind && t.message === message);
        if (same) return same.id;
      }
      const id = nextId.current++;
      const item = { id, kind, message };
      visible.current = [...visible.current, item];
      setToasts((list) => [...list, item]);
      const duration = options?.duration ?? (kind === "error" ? ERROR_TOAST_DURATION : TOAST_DURATION);
      if (duration > 0) {
        const timer: Timer = { handle: null, remaining: duration, startedAt: Date.now() };
        if (!paused.current.hover && !paused.current.focus) timer.handle = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  const setPaused = useCallback(
    (reason: "hover" | "focus", on: boolean) => {
      const wasPaused = paused.current.hover || paused.current.focus;
      paused.current = { ...paused.current, [reason]: on };
      const isPaused = paused.current.hover || paused.current.focus;
      if (wasPaused === isPaused) return;
      const now = Date.now();
      for (const [id, t] of timers.current) {
        if (isPaused && t.handle) {
          clearTimeout(t.handle);
          t.handle = null;
          t.remaining = Math.max(0, t.remaining - (now - t.startedAt));
        } else if (!isPaused && !t.handle) {
          t.startedAt = now;
          t.handle = setTimeout(() => dismiss(id), t.remaining);
        }
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) if (t.handle) clearTimeout(t.handle);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push("success", m, o),
      error: (m, o) => push("error", m, o),
      info: (m, o) => push("info", m, o),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <section
          className={styles.stack}
          aria-label="Notifications"
          aria-live="polite"
          onMouseEnter={() => setPaused("hover", true)}
          onMouseLeave={() => setPaused("hover", false)}
          onFocus={() => setPaused("focus", true)}
          onBlur={(e: FocusEvent<HTMLElement>) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused("focus", false);
          }}
        >
          {toasts.map((t) => (
            <div key={t.id} role={t.kind === "error" ? "alert" : "status"} className={cx(styles.toast, styles[t.kind])}>
              <span className={styles.message}>{t.message}</span>
              <button type="button" className={styles.close} aria-label="Dismiss" onClick={() => dismiss(t.id)}>
                <Icon name="close" size={16} strokeWidth={2} />
              </button>
            </div>
          ))}
        </section>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
